/*
 * $Id:: DropdownFilters.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';

import { globals as globalsSlice } from '../../app/globalsSlice';
import { t } from '../../providers';
import UAMultiRangePicker from '../UAMultiRangePicker';
import UAMultiFilter from './UAMultiFilter';

const useStyles = makeStyles((theme) => ({
    dropdownFilters: {
        display: 'flex',
        flexFlow: 'row wrap',
        alignItems: 'center',
        '@media (max-width:960px)': {
            display: 'grid',
            flexFlow: 'row nowrap',
        },
    },
    filterLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: theme.palette.text.tableValue,
        marginRight: '6px',
    },
    dropdownDates: {
        width: '9rem',
        marginBottom: '2px',
        '@media (max-width:960px)': {
            marginTop: '11px',
            marginBottom: 'unset',
        },
    },
}));

const DropdownFilters = ({
    isTablet,
    filterItems,
    onUpdateFilters,
    filters,
    applicationType,
    datesOptions,
    filtersChips,
    authorities,
    filterType,
    myApplicationsFilters,
    page,
}) => {
    const classes = useStyles();
    const [filtersObject, setFiltersObject] = useState([]);
    const [dateRanges, setDateRanges] = useState([]);
    const [availableFilterOptions, setAvailableFilterOptions] = useState({});
    const [datesFilterOptions, setDatesFilterOptions] = useState([]);
    const { filtersOptions } = useSelector(globalsSlice);

    const locarnoFilter = (rest) => {
        // Designs Locarno Classes
        let filterOptions = [t('all.classes')];
        const locarnoClasses = availableFilterOptions[rest.name].classification.map((item) => item.classCode);
        filterOptions = filterOptions.concat(locarnoClasses);
        return filterOptions;
    };

    const formTypesFilter = () => {
        let filterOptions = [];
        let ipRT = !!filters.ipRightType && filters.ipRightType.length ? filters.ipRightType : ['Trademarks', 'Designs'];
        if (ipRT.includes('Trademarks')) {
            filterOptions.push(...(availableFilterOptions.tmFilteringTypes || []));
        }
        if (ipRT.includes('Designs')) {
            filterOptions.push(...(availableFilterOptions.dsFilteringTypes || []));
        }
        return filterOptions;
    };

    const filtersRender = (filterItems) => {
        return filterItems
            .getDropDownFilters(applicationType || filterType)
            .filter(({ name: filterName }) => (authorities ? authorities.includes(filterName) : true))
            .filter(
                ({ globalsKey: filterName }) =>
                    ['formTypes', 'classes'].includes(filterName) || Object.keys(availableFilterOptions).includes(filterName)
            )
            .map(({ multiple, globalsKey, ...rest }) => {
                const filterValue = filters[rest.name];
                let filterOptions = availableFilterOptions[globalsKey] || rest.filterOptions || [];
                if (applicationType === 'design' && rest.name === 'locarno') {
                    filterOptions = locarnoFilter(rest); // Designs Locarno Classes
                } else if (applicationType === 'eservice' && rest.name === 'ipRightType') {
                    filterOptions = [{ key: '', label: 'all.ip.types' }].concat(filterOptions); // e-Services IP right Type
                } else if (applicationType === 'eservice' && rest.name === 'formTypes') {
                    filterOptions = formTypesFilter(); // e-Services Form Types
                } else if (!multiple) {
                    filterOptions = [{ key: '', label: 'filter.all' }].concat(filterOptions); // reply Needed in correspondence
                }
                return {
                    ...rest,
                    multiple,
                    value: filterValue,
                    filterOptions,
                };
            });
    };

    useEffect(() => {
        const options = page === 'myApplications' ? myApplicationsFilters : filtersOptions;
        if (options && !!Object.keys(options).length) {
            setAvailableFilterOptions(options[applicationType || filterType] || {});
        }
    }, [myApplicationsFilters, applicationType]);

    useEffect(() => {
        setDatesFilterOptions(availableFilterOptions[datesOptions] || []);
        const hasDateTypes = availableFilterOptions && availableFilterOptions[datesOptions] && availableFilterOptions[datesOptions].length;
        const newDateRanges = hasDateTypes ? [{ dateType: availableFilterOptions[datesOptions[0]] }] : [];
        setDateRanges(newDateRanges);
    }, [availableFilterOptions, datesOptions]);

    useEffect(() => {
        if (!!Object.keys(availableFilterOptions).length) {
            const filtersToRender = filtersRender(filterItems);

            const { dates } = filters;
            if (dates) {
                setDateRanges(dates);
            } else {
                setDateRanges(null);
            }

            setFiltersObject(filtersToRender);
        }
    }, [availableFilterOptions, filters, applicationType]);

    const handleFilterChange = (event, optionSelected) => {
        const idx = filtersObject.findIndex(({ name }) => event.target.name === name);
        if (idx > -1) {
            const updatedFilters = [...filtersObject];
            updatedFilters[idx].value = event.target.value;
            setFiltersObject(updatedFilters);

            const updatedValues = { ...filters, [event.target.name]: event.target.value };
            const filterChips = { ...filtersChips, [event.target.name]: optionSelected };

            onUpdateFilters(updatedValues, filterChips);
        }
    };

    const handleDateFilterChange = (ranges) => {
        setDateRanges(ranges);
    };

    const onCloseDatepicker = () => {
        const selectedRanges = dateRanges.filter((item) => item.dateType && item.startDate && item.endDate);

        if (selectedRanges.length > 0) {
            onUpdateFilters({ ...filters, dates: selectedRanges }, { ...filtersChips, dates: selectedRanges });
        } else {
            if (filters.dates && Object.keys(filters.dates).length !== 0) {
                // check previous Filters for dates
                const updatedFilters = { ...filters };
                const updatedFiltersChips = { ...filtersChips };
                delete updatedFilters['dates'];
                delete updatedFiltersChips['dates'];
                onUpdateFilters(updatedFilters, updatedFiltersChips);
            }
        }
    };

    return (
        <div className={classes.dropdownFilters}>
            {!isTablet && <InputLabel className={classes.filterLabel}>{t('filters')}:</InputLabel>}

            {(filtersObject || []).map((filter, index) => {
                return (
                    <React.Fragment key={`${filter.filterid}_${index}`}>
                        {filter.name.toLowerCase().includes('date') ? (
                            <UAMultiRangePicker
                                className={classes.dropdownDates}
                                id={'dateRanges'}
                                dropdownOptions={datesFilterOptions}
                                onUpdateRanges={handleDateFilterChange}
                                onClose={onCloseDatepicker}
                                ranges={dateRanges}
                            />
                        ) : (
                            <UAMultiFilter filter={filter} handleFilterChange={handleFilterChange} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default DropdownFilters;
